import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { fromJS } from 'immutable';
import * as _ from 'lodash';
import validator from 'validator';
import ReactResizeDetector from 'react-resize-detector';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import injectReducer from 'utils/injectReducer';
import { makeSelectADAndDBenefitsTugo, makeSelectADAndDBenefitsTugoErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import WizardNavigationButtons from '../components/WizardNavigationButtons';
import { handleFormDataChange, nextSlide, resetFormFields } from './actions';
import { ErrorMessage, Validate, ValidateGroup } from '../../../components/Validation';
import { saveFormData } from '../actions';
import { AdditionalList } from './components/Additional';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { required } from '../../../utils/validation/validator';
import { makeSelectInsuredsList, makeSelectIsClaimForFieldEnabled, makeSelectFormHasChanges, makeSelectPolicyTypeCode, makeSelectPolicyPlanIds } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';
import { stepADAndDTugoLookup, stepADAndDTravellerTugoLookup } from '../dataMapper/claimCodeMapper';
import { LIST_POLICY_TYPE_CODE } from '../../App/constants';
import FNOLTooltip from '../../../components/FNOLTooltip';

/** Claim codes for AD&D step: VTC-AFCC, VTC-24HACC */
export const AD_AND_D_CLAIM_CODES = ['VTC-AFCC', 'VTC-24HACC'];

const OPTION_AIR_FLIGHT = 'Air Flight/Common Carrier Accident';
const OPTION_24_HOUR = '24-Hour Accident';

export class ADAndDBenefitsTugo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = _.uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onChange = (propertyName) => (event) => {
    const propertyFullName = `form.${propertyName}`;
    if (event === null) {
      this.props.onFormDataChange(propertyFullName, null);
      this.props.setFormHasChanges(true);
      return;
    }

    if (event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyFullName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      this.props.onFormDataChange(propertyFullName, value);
    }
    this.props.setFormHasChanges(true);
  };

  handleExpenseTypeChange = (formField) => (data) => {
    this.props.onFormDataChange(`form.${formField}`, fromJS(data));
    this.props.setFormHasChanges(true);
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  handleExpensesSelected = (propertyName) => (event) => {
    const { whatExpensesAreYouClaimingFor } = this.props.form;
    const expensesSelected = Array.isArray(whatExpensesAreYouClaimingFor) ? [...whatExpensesAreYouClaimingFor] : [];
    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (value) {
      if (!expensesSelected.includes(name)) expensesSelected.push(name);
    } else {
      const idx = expensesSelected.indexOf(name);
      if (idx !== -1) expensesSelected.splice(idx, 1);
      if (name === OPTION_AIR_FLIGHT) {
        this.props.onResetFormFields(['airFlightCommonCarrierAccidentItems']);
      } else if (name === OPTION_24_HOUR) {
        this.props.onResetFormFields(['accident24HourItems']);
      }
    }
    this.props.onFormDataChange(propertyName, expensesSelected);
    this.props.setFormHasChanges(true);
  };

  handleAccidentalParentCheckbox = (event) => {
    const isChecked = event.target.checked;
    this.props.onFormDataChange('form.accidentalDeathAndDismembermentParentSelected', isChecked);

    if (!isChecked) {
      const { whatExpensesAreYouClaimingFor } = this.props.form;
      const expensesSelected = Array.isArray(whatExpensesAreYouClaimingFor)
        ? whatExpensesAreYouClaimingFor
        : [];

      const updatedSelected = expensesSelected.filter(
        (name) => name !== OPTION_AIR_FLIGHT && name !== OPTION_24_HOUR
      );

      this.props.onFormDataChange('form.whatExpensesAreYouClaimingFor', updatedSelected);
      this.props.onResetFormFields(['airFlightCommonCarrierAccidentItems', 'accident24HourItems']);
    }

    this.props.setFormHasChanges(true);
  };

  getAirFlightTooltipContent() {
    const { intl } = this.props;
    const tooltip = intl.formatMessage(messages.airFlightCommonCarrierAccidentTooltip).trim();
    const requiredPrefix = 'Death or dismemberment as a result of an ';
    const hasPrefix = tooltip.toLowerCase().startsWith(requiredPrefix.toLowerCase());

    return hasPrefix ? tooltip : `${requiredPrefix}${tooltip}`;
  }

  onResize() {
    const { adjust } = this.props;
    if (adjust) adjust();
  }

  appendIds = (array) =>
    (array || []).map((item) => {
      const newItem = { ...item };
      if (!newItem.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  validateTableField(groups) {
    return _.some(groups, (fields) =>
      _.some(fields, (value) => {
        const newValue = value ? value.toString() : '';
        return validator.isEmpty(newValue);
      })
    );
  }

  getValidationErrors() {
    const { whatExpensesAreYouClaimingFor, airFlightCommonCarrierAccidentItems, accident24HourItems } = this.props.form;
    const airFlightItems = Array.isArray(airFlightCommonCarrierAccidentItems) ? airFlightCommonCarrierAccidentItems : [];
    const accident24Items = Array.isArray(accident24HourItems) ? accident24HourItems : [];
    const expensesSelected = Array.isArray(whatExpensesAreYouClaimingFor) ? whatExpensesAreYouClaimingFor : [];
    const errors = [];

    if (expensesSelected.length === 0) {
      errors.push({ field: 'whatExpensesAreYouClaimingFor', message: 'At least one expense type is required.' });
    }
    if (expensesSelected.includes(OPTION_AIR_FLIGHT) && this.validateTableField([airFlightItems])) {
      errors.push({ field: 'airFlightCommonCarrierAccidentItems', message: 'Expense details are required for Air Flight/Common Carrier Accident.' });
    }
    if (expensesSelected.includes(OPTION_24_HOUR) && this.validateTableField([accident24Items])) {
      errors.push({ field: 'accident24HourItems', message: 'Expense details are required for 24-Hour Accident.' });
    }

    return errors;
  }

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') return;
    if (e.key === 'Tab') e.preventDefault();
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.props.onFormDataSave();
    if (this.nextButton) this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState((s) => ({ validationCounter: s.validationCounter + 1 }));
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState((s) => ({ validationCounter: s.validationCounter + 1 }));
    } else {
      this.props.makeValid(this.props.step);
    }

    this.props.onFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  renderExpensesItems(formField) {
    const items = this.props.form[formField];
    const itemsArray = Array.isArray(items) ? items : [];
    const newMedicalExpenseItems = this.appendIds(itemsArray);
    const { isSummary, submitted, insuredsList, isClaimForFieldEnabled, intl } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="mt-2">
        <AdditionalList
          data={newMedicalExpenseItems}
          onChange={this.handleExpenseTypeChange(formField)}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
          insuredsList={insuredsList}
          isClaimForFieldEnabled={isClaimForFieldEnabled}
          intl={intl}
        />
      </div>
    );
  }

  render() {
    const { isSummary, submitted, loading, intl, policyTypeCode, policyPlanIds } = this.props;
    const normalizedPolicyPlanIds = Array.isArray(policyPlanIds)
      ? policyPlanIds
      : (policyPlanIds && typeof policyPlanIds.toJS === 'function' ? policyPlanIds.toJS() : []);
    const isADAndDTravellerPolicy = LIST_POLICY_TYPE_CODE.LIST_POLICY_PLAN_ID_AD_AND_D_TRAVELLER.includes(policyTypeCode)
      || normalizedPolicyPlanIds.some((planId) => LIST_POLICY_TYPE_CODE.LIST_POLICY_PLAN_ID_AD_AND_D_TRAVELLER.includes(planId));
    const lookup = (isADAndDTravellerPolicy ? stepADAndDTravellerTugoLookup : stepADAndDTugoLookup) || {};
    const isRenderAirFlight = !!(lookup.airFlightCommonCarrierAccident && lookup.airFlightCommonCarrierAccident.claimCode);
    const isRenderAccident24Hour = !!(lookup.accident24Hour && lookup.accident24Hour.claimCode);
    const isRenderAccidentalBenefitsGroup = isRenderAirFlight || isRenderAccident24Hour;

    const form = isSummary ? _.mapValues(this.props.form, (v) => (v == null ? 'N/A' : v)) : this.props.form;
    const whatExpensesAreYouClaimingFor = form.whatExpensesAreYouClaimingFor;
    const isReadOnly = submitted || isSummary;

    const expensesSelected = Array.isArray(whatExpensesAreYouClaimingFor) ? whatExpensesAreYouClaimingFor : [];
    const accidentalDeathAndDismembermentParentSelected =
      form.accidentalDeathAndDismembermentParentSelected === true;

    return (
      <div className="app-content">
        <Validate
          name="whatExpensesAreYouClaimingFor"
          validators={[required]}
          group={this.validatorGroup}
          addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
          validChangeInGroup={this.validatorGroup.validChangeInGroup}
          validationCounter={this.state.validationCounter}
          validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
          defaultValue={expensesSelected}
          fieldName={messages.whatExpensesAreYouClaimingFor}
        >
          <div className="row">
            {isRenderAccidentalBenefitsGroup && (
              <div className="col-sm-12 form-group pt-3">
                <div className="pt-2">
                  <input
                    disabled={isReadOnly}
                    type="checkbox"
                    name="Accidental Death and Dismemberment Benefits"
                    checked={accidentalDeathAndDismembermentParentSelected}
                    onChange={this.handleAccidentalParentCheckbox}
                  />
                  <span>
                    <FormattedMessage {...messages.accidentalDeathAndDismembermentBenefitsGroup} />
                  </span>
                </div>
                {accidentalDeathAndDismembermentParentSelected && (
                  <div className="col-sm-12 form-group pt-2">
                    {isRenderAirFlight && (
                      <div>
                        <div className="pt-2 ml-2">
                          <input
                            disabled={isReadOnly}
                            type="checkbox"
                            name={OPTION_AIR_FLIGHT}
                            checked={expensesSelected.includes(OPTION_AIR_FLIGHT)}
                            onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                          />
                          <span>
                            <FormattedMessage {...messages.airFlightCommonCarrierAccident} />{' '}
                            <FNOLTooltip
                              dataFor="tugoADAndDAirFlightTooltip"
                              content={this.getAirFlightTooltipContent()}
                              place="right"
                            />
                          </span>
                        </div>
                        {expensesSelected.includes(OPTION_AIR_FLIGHT) && (
                          <div className="col-sm-12 form-group ml-2 mt-2 p-3 bg-light rounded">
                            {this.renderExpensesItems('airFlightCommonCarrierAccidentItems')}
                          </div>
                        )}
                      </div>
                    )}

                    {isRenderAccident24Hour && (
                      <div>
                        <div className="pt-2 ml-2">
                          <input
                            disabled={isReadOnly}
                            type="checkbox"
                            name={OPTION_24_HOUR}
                            checked={expensesSelected.includes(OPTION_24_HOUR)}
                            onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                          />
                          <span>
                            <FormattedMessage {...messages.accident24Hour} />{' '}
                            <FNOLTooltip
                              dataFor="tugoADAndD24HourTooltip"
                              content={intl.formatMessage(messages.accident24HourTooltip)}
                              place="right"
                            />
                          </span>
                        </div>
                        {expensesSelected.includes(OPTION_24_HOUR) && (
                          <div className="col-sm-12 form-group ml-2 mt-2 p-3 bg-light rounded">
                            {this.renderExpensesItems('accident24HourItems')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <ErrorMessage for="required">
            <FormattedMessage {...messages.isRequired} values={messages.whatExpensesAreYouClaimingFor} />
          </ErrorMessage>
        </Validate>

        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleSaveButtonClick}
            canMoveNext
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            showSpinnerForElement={this.showSpinnerForElement}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => { this.nextButton = ref; }}
          />
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

ADAndDBenefitsTugo.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  onResetFormFields: PropTypes.func,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  errors: PropTypes.any,
  loading: PropTypes.bool,
  currentStep: PropTypes.number,
  step: PropTypes.number,
  adjust: PropTypes.func,
  onNext: PropTypes.func,
  makeError: PropTypes.func,
  makeValid: PropTypes.func,
  isReadOnly: PropTypes.bool,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  insuredsList: PropTypes.any,
  isClaimForFieldEnabled: PropTypes.bool,
  intl: intlShape.isRequired,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
  policyTypeCode: PropTypes.string,
  policyPlanIds: PropTypes.any,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectADAndDBenefitsTugo(),
  errors: makeSelectADAndDBenefitsTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  insuredsList: makeSelectInsuredsList(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  hasFormChanges: makeSelectFormHasChanges(),
  policyTypeCode: makeSelectPolicyTypeCode(),
  policyPlanIds: makeSelectPolicyPlanIds(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) => dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetFormFields: (fields) => dispatch(resetFormFields(fields)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withReducer = injectReducer({
  key: 'adAndDBenefitsTugo',
  reducer,
});

export default compose(
  withReducer,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
)(ADAndDBenefitsTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.ADAndDBenefits/index.js